import React, { useState, useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Play, Pause, ZoomIn, ZoomOut, Maximize2, RotateCcw, Video, Download, Edit3, Layers, GitBranch, AlertCircle, ArrowRight, MapPin, Navigation, TrendingUp } from 'lucide-react';

interface PathAnalysisControls {
  playing: boolean;
  playbackRate: number;
  progress: number;
  zoom: number;
}

interface AnalysisMarker {
  id: string;
  timestamp: number;
  x: number;
  y: number;
  type: 'dwell' | 'interaction' | 'anomaly';
  note?: string;
}

interface ODPair {
  origin: string;
  destination: string;
  count: number;
  routes: { id: string; waypoints: string[]; detours: number }[];
}

interface WalkShed {
  amenity: string;
  radius30s: number;
  radius60s: number;
  radius90s: number;
  coveredSeats: number;
  underservedSeats: number;
}

interface WayfindingScore {
  zone: string;
  floor: string;
  backtracks: number;
  deadEnds: number;
  totalPaths: number;
  score: number;
  remediation: string;
}

interface LeaseLeakage {
  fromTenant: string;
  toTenant: string;
  crossings: number;
  avgDuration: string;
}

const PathAnalysisStudio: React.FC = () => {
  const [controls, setControls] = useState<PathAnalysisControls>({
    playing: false,
    playbackRate: 1,
    progress: 0,
    zoom: 1,
  });

  const [activeMode, setActiveMode] = useState<'view' | 'analyze' | 'annotate'>('view');
  const [activeTab, setActiveTab] = useState<'routes' | 'walksheds' | 'wayfinding' | 'lease'>('routes');
  const [markers, setMarkers] = useState<AnalysisMarker[]>([]);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [selectedODPair, setSelectedODPair] = useState<string | null>(null);
  const [selectedAmenity, setSelectedAmenity] = useState<'cafe' | 'focus' | 'restroom'>('cafe');
  
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (controls.playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setControls(prev => ({ ...prev, playing: !prev.playing }));
    }
  };

  const handleProgress = () => {
    if (videoRef.current) {
      const progress = videoRef.current.currentTime / videoRef.current.duration;
      setControls(prev => ({ ...prev, progress }));
    }
  };

  const handleSpeedChange = (speed: number) => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = speed;
      setControls(prev => ({ ...prev, playbackRate: speed }));
    }
  };

  const handleMarkerAdd = (event: React.MouseEvent) => {
    if (activeMode !== 'annotate') return;
    
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    const newMarker: AnalysisMarker = {
      id: Date.now().toString(),
      timestamp: controls.progress,
      x,
      y,
      type: 'dwell',
    };
    
    setMarkers(prev => [...prev, newMarker]);
  };

  // Sample O-D data
  const odPairs: ODPair[] = [
    {
      origin: 'North Entrance',
      destination: 'Open Desks 3A',
      count: 42,
      routes: [
        { id: 'r1', waypoints: ['North Entrance', 'Lobby', 'Elevator', 'Floor 3', 'Open Desks 3A'], detours: 0 },
        { id: 'r2', waypoints: ['North Entrance', 'Lobby', 'Cafe', 'Elevator', 'Floor 3', 'Open Desks 3A'], detours: 1 }
      ]
    },
    {
      origin: 'South Entrance',
      destination: 'Meeting Rooms 2B',
      count: 28,
      routes: [
        { id: 'r3', waypoints: ['South Entrance', 'Stairwell A', 'Floor 2', 'Meeting Rooms 2B'], detours: 0 }
      ]
    },
    {
      origin: 'Open Desks 3A',
      destination: 'Cafe',
      count: 38,
      routes: [
        { id: 'r4', waypoints: ['Open Desks 3A', 'Floor 3 Corridor', 'Elevator', 'Cafe'], detours: 0 },
        { id: 'r5', waypoints: ['Open Desks 3A', 'Floor 3 Corridor', 'Restroom', 'Elevator', 'Cafe'], detours: 1 }
      ]
    },
    {
      origin: 'Meeting Rooms 2B',
      destination: 'Focus Pods 1C',
      count: 15,
      routes: [
        { id: 'r6', waypoints: ['Meeting Rooms 2B', 'Stairwell B', 'Floor 1', 'Focus Pods 1C'], detours: 0 }
      ]
    }
  ];

  // Walk-shed data
  const walkSheds: WalkShed[] = [
    {
      amenity: 'Cafe',
      radius30s: 45,
      radius60s: 82,
      radius90s: 118,
      coveredSeats: 152,
      underservedSeats: 28
    },
    {
      amenity: 'Focus Rooms',
      radius30s: 38,
      radius60s: 72,
      radius90s: 95,
      coveredSeats: 95,
      underservedSeats: 85
    },
    {
      amenity: 'Restrooms',
      radius30s: 52,
      radius60s: 98,
      radius90s: 142,
      coveredSeats: 168,
      underservedSeats: 12
    }
  ];

  // Wayfinding scores
  const wayfindingScores: WayfindingScore[] = [
    {
      zone: 'North Wing',
      floor: 'Floor 3',
      backtracks: 8,
      deadEnds: 3,
      totalPaths: 142,
      score: 92,
      remediation: 'Clear signage at elevator bank'
    },
    {
      zone: 'Conference Center',
      floor: 'Floor 2',
      backtracks: 22,
      deadEnds: 12,
      totalPaths: 98,
      score: 65,
      remediation: 'Add wayfinding at corridor split, door labels'
    },
    {
      zone: 'East Wing',
      floor: 'Floor 1',
      backtracks: 15,
      deadEnds: 8,
      totalPaths: 124,
      score: 81,
      remediation: 'Clarify restroom signage'
    },
    {
      zone: 'South Annex',
      floor: 'Floor 2',
      backtracks: 34,
      deadEnds: 18,
      totalPaths: 86,
      score: 39,
      remediation: 'Major signage overhaul, consider floor plan at entry'
    }
  ];

  // Lease leakage data
  const leaseLeakage: LeaseLeakage[] = [
    { fromTenant: 'Tenant A (Floors 1-2)', toTenant: 'Tenant B (Floor 3)', crossings: 24, avgDuration: '32 sec' },
    { fromTenant: 'Tenant B (Floor 3)', toTenant: 'Shared Amenity (Floor 1)', crossings: 58, avgDuration: '18 sec' },
    { fromTenant: 'Tenant C (Floor 4)', toTenant: 'Tenant A (Floors 1-2)', crossings: 12, avgDuration: '45 sec' }
  ];

  const selectedAmenityData = walkSheds.find(w => w.amenity === 
    (selectedAmenity === 'cafe' ? 'Cafe' : selectedAmenity === 'focus' ? 'Focus Rooms' : 'Restrooms')
  );

  return (
    <div className="p-6 space-y-6" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Path Studio
        </h2>
        <p style={{ color: 'var(--sca-muted)' }}>
          Full walking path reconstruction from flooring sensors. Sample path data, illustrative
        </p>
      </div>

      {/* Disclaimer */}
      <div className="rounded-lg border p-4" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
        <div className="flex items-start gap-3">
          <AlertCircle size={18} style={{ color: '#8a7355', flexShrink: 0, marginTop: 2 }} />
          <div className="text-sm" style={{ color: '#8a7355' }}>
            <strong>Path Reconstruction vs Heatmaps:</strong> Flooring sensors reconstruct complete walking paths, not just occupancy density heatmaps. 
            See individual path direction, dwell points, interaction zones, and movement sequence for space planning and behavioral analysis.
          </div>
        </div>
      </div>

      {/* Video Studio */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--sca-ink)' }}>Path Reconstruction Studio</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setActiveMode('view')}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor: activeMode === 'view' ? 'var(--sca-teal)' : 'white',
                color: activeMode === 'view' ? 'white' : 'var(--sca-ink)',
                border: `1px solid ${activeMode === 'view' ? 'var(--sca-teal)' : 'var(--sca-border)'}`
              }}
            >
              <div className="flex items-center gap-2">
                <Video size={16} />
                View
              </div>
            </button>
            <button
              onClick={() => setActiveMode('analyze')}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor: activeMode === 'analyze' ? 'var(--sca-teal)' : 'white',
                color: activeMode === 'analyze' ? 'white' : 'var(--sca-ink)',
                border: `1px solid ${activeMode === 'analyze' ? 'var(--sca-teal)' : 'var(--sca-border)'}`
              }}
            >
              <div className="flex items-center gap-2">
                <Layers size={16} />
                Analyze
              </div>
            </button>
            <button
              onClick={() => setActiveMode('annotate')}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor: activeMode === 'annotate' ? 'var(--sca-teal)' : 'white',
                color: activeMode === 'annotate' ? 'white' : 'var(--sca-ink)',
                border: `1px solid ${activeMode === 'annotate' ? 'var(--sca-teal)' : 'var(--sca-border)'}`
              }}
            >
              <div className="flex items-center gap-2">
                <Edit3 size={16} />
                Annotate
              </div>
            </button>
          </div>
        </div>

        {/* Video Player */}
        <div className="relative rounded-lg overflow-hidden" style={{ border: '1px solid var(--sca-border)' }}>
          <TransformWrapper>
            <TransformComponent>
              <div className="relative" onClick={handleMarkerAdd}>
                <video
                  ref={videoRef}
                  src="/videos/path_renderings.mp4"
                  onTimeUpdate={handleProgress}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                  playsInline
                  muted
                />
                {markers.map(marker => (
                  <div
                    key={marker.id}
                    className="absolute w-3 h-3 rounded-full transform -translate-x-1/2 -translate-y-1/2"
                    style={{
                      left: marker.x,
                      top: marker.y,
                      backgroundColor: '#c33',
                      border: '2px solid white'
                    }}
                    title={marker.note || `Marker at ${Math.round(marker.timestamp * 100)}%`}
                  />
                ))}
                {showHeatmap && (
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to right, rgba(10, 155, 155, 0.2), rgba(199, 51, 51, 0.2))'
                    }}
                  />
                )}
              </div>
            </TransformComponent>
          </TransformWrapper>

          {/* Video Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-4" style={{ backgroundColor: 'rgba(26, 26, 26, 0.9)' }}>
            <div className="flex items-center justify-between text-white mb-2">
              <div className="flex items-center gap-4">
                <button
                  onClick={handlePlayPause}
                  className="transition-opacity hover:opacity-80"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {controls.playing ? <Pause size={24} style={{ color: 'white' }} /> : <Play size={24} style={{ color: 'white' }} />}
                </button>
                <select
                  value={controls.playbackRate}
                  onChange={(e) => handleSpeedChange(Number(e.target.value))}
                  className="px-2 py-1 rounded text-sm"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value={0.5} style={{ backgroundColor: '#1a1a1a' }}>0.5x</option>
                  <option value={1} style={{ backgroundColor: '#1a1a1a' }}>1x</option>
                  <option value={1.5} style={{ backgroundColor: '#1a1a1a' }}>1.5x</option>
                  <option value={2} style={{ backgroundColor: '#1a1a1a' }}>2x</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowHeatmap(!showHeatmap)}
                  className="transition-opacity hover:opacity-80"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: showHeatmap ? 'var(--sca-teal)' : 'white'
                  }}
                  title="Toggle heatmap overlay"
                >
                  <Layers size={20} />
                </button>
                <button
                  onClick={() => {
                    const video = videoRef.current;
                    if (video) {
                      video.currentTime = 0;
                    }
                  }}
                  className="transition-opacity hover:opacity-80"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }}
                  title="Restart"
                >
                  <RotateCcw size={20} />
                </button>
                <button
                  onClick={() => {}}
                  className="transition-opacity hover:opacity-80"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }}
                  title="Export"
                >
                  <Download size={20} />
                </button>
              </div>
            </div>
            <div className="w-full rounded-full h-1" style={{ backgroundColor: '#444' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${controls.progress * 100}%`,
                  backgroundColor: 'var(--sca-teal)'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Tabs */}
      <div className="flex gap-2 border-b" style={{ borderColor: 'var(--sca-border)' }}>
        <button
          onClick={() => setActiveTab('routes')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'routes' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'routes' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Routes (O-D)
        </button>
        <button
          onClick={() => setActiveTab('walksheds')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'walksheds' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'walksheds' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Walk-sheds
        </button>
        <button
          onClick={() => setActiveTab('wayfinding')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'wayfinding' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'wayfinding' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Wayfinding
        </button>
        <button
          onClick={() => setActiveTab('lease')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'lease' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'lease' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Lease Overlay
        </button>
      </div>

      {/* Tab Content */}
      
      {/* Routes Tab (O-D Sankey) */}
      {activeTab === 'routes' && (
        <div className="space-y-6">
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
              Origin to Destination Flow (10 AM Hour)
            </h3>
            <p className="text-sm mb-6" style={{ color: 'var(--sca-muted)' }}>
              Top O-D pairs with reconstructed routes. Heatmaps stop at busy zones; path reconstruction shows which route people took.
            </p>

            {/* Sankey-style horizontal flow */}
            <div className="space-y-4">
              {odPairs.map((pair, idx) => {
                const isSelected = selectedODPair === `${pair.origin}-${pair.destination}`;
                const barWidth = (pair.count / 50) * 100;
                
                return (
                  <div key={idx}>
                    <div
                      className="p-4 rounded-lg border cursor-pointer transition-colors"
                      style={{
                        backgroundColor: isSelected ? 'var(--sca-hover)' : 'white',
                        borderColor: isSelected ? 'var(--sca-teal)' : 'var(--sca-border)'
                      }}
                      onClick={() => setSelectedODPair(isSelected ? null : `${pair.origin}-${pair.destination}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3 flex-1">
                          <span className="font-medium" style={{ color: 'var(--sca-ink)', minWidth: 180 }}>{pair.origin}</span>
                          <ArrowRight size={16} style={{ color: 'var(--sca-muted)' }} />
                          <span className="font-medium" style={{ color: 'var(--sca-ink)', minWidth: 180 }}>{pair.destination}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-lg" style={{ color: 'var(--sca-teal)' }}>{pair.count}</span>
                          <span className="text-sm ml-1" style={{ color: 'var(--sca-muted)' }}>paths</span>
                        </div>
                      </div>
                      <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--sca-border)' }}>
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${barWidth}%`,
                            backgroundColor: 'var(--sca-teal)'
                          }}
                        />
                      </div>
                    </div>

                    {isSelected && (
                      <div className="mt-3 ml-6 rounded-lg border p-4" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
                        <div className="text-xs font-semibold mb-3" style={{ color: '#8a7355' }}>
                          RECONSTRUCTED ROUTES ({pair.routes.length} variants)
                        </div>
                        {pair.routes.map((route, ridx) => (
                          <div key={route.id} className="mb-3 last:mb-0">
                            <div className="flex items-start gap-2 text-sm">
                              <span className="font-semibold" style={{ color: '#8a7355' }}>Route {ridx + 1}:</span>
                              <div className="flex flex-wrap items-center gap-1">
                                {route.waypoints.map((wp, wpidx) => (
                                  <React.Fragment key={wpidx}>
                                    <span style={{ color: 'var(--sca-ink)' }}>{wp}</span>
                                    {wpidx < route.waypoints.length - 1 && (
                                      <ArrowRight size={12} style={{ color: '#8a7355' }} />
                                    )}
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                            {route.detours > 0 && (
                              <div className="text-xs mt-1" style={{ color: '#8a7355' }}>
                                {route.detours} detour(s) via amenity
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Walk-sheds Tab */}
      {activeTab === 'walksheds' && (
        <div className="space-y-6">
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
              Amenity Walk-shed from Measured Paths
            </h3>
            <p className="text-sm mb-6" style={{ color: 'var(--sca-muted)' }}>
              30/60/90 second walk-sheds from amenities using measured path speeds (not crow-flies). Quantify underserved seats.
            </p>

            <div className="flex gap-2 mb-6">
              {(['cafe', 'focus', 'restroom'] as const).map((amenity) => (
                <button
                  key={amenity}
                  onClick={() => setSelectedAmenity(amenity)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: selectedAmenity === amenity ? 'var(--sca-teal)' : 'white',
                    color: selectedAmenity === amenity ? 'white' : 'var(--sca-ink)',
                    border: `1px solid ${selectedAmenity === amenity ? 'var(--sca-teal)' : 'var(--sca-border)'}`
                  }}
                >
                  {amenity === 'cafe' ? 'Cafe' : amenity === 'focus' ? 'Focus Rooms' : 'Restrooms'}
                </button>
              ))}
            </div>

            {selectedAmenityData && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="rounded-lg border p-4" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
                    <div className="text-sm font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>Walk-shed Coverage</div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B88', borderRadius: 2 }}></div>
                          <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>30 second walk</span>
                        </div>
                        <span className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{selectedAmenityData.radius30s} seats</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B66', borderRadius: 2 }}></div>
                          <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>60 second walk</span>
                        </div>
                        <span className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{selectedAmenityData.radius60s} seats</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B44', borderRadius: 2 }}></div>
                          <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>90 second walk</span>
                        </div>
                        <span className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{selectedAmenityData.radius90s} seats</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-badge-bg)', borderColor: 'var(--sca-teal-muted)' }}>
                      <div className="text-xs mb-1" style={{ color: 'var(--sca-muted)' }}>Covered Seats</div>
                      <div className="text-2xl font-bold" style={{ color: 'var(--sca-teal)' }}>{selectedAmenityData.coveredSeats}</div>
                      <div className="text-xs" style={{ color: 'var(--sca-teal)' }}>within 90s walk</div>
                    </div>
                    <div className="rounded-lg border p-4" style={{ backgroundColor: '#fee', borderColor: '#fcc' }}>
                      <div className="text-xs mb-1" style={{ color: '#c33' }}>Underserved</div>
                      <div className="text-2xl font-bold" style={{ color: '#c33' }}>{selectedAmenityData.underservedSeats}</div>
                      <div className="text-xs" style={{ color: '#c33' }}>beyond 90s walk</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border p-4" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
                  <div className="text-xs font-semibold mb-3" style={{ color: '#8a7355' }}>SPACE PLANNING IMPACT</div>
                  <div className="space-y-3 text-sm" style={{ color: '#8a7355' }}>
                    <p>
                      <strong>Coverage:</strong> {Math.round((selectedAmenityData.coveredSeats / (selectedAmenityData.coveredSeats + selectedAmenityData.underservedSeats)) * 100)}% of desks within 90 second walk.
                    </p>
                    <p>
                      <strong>Underserved zone:</strong> {selectedAmenityData.underservedSeats} seats beyond walk-shed threshold require {Math.ceil(selectedAmenityData.underservedSeats / 40)} additional {selectedAmenityData.amenity.toLowerCase()} location(s) or seat redistribution.
                    </p>
                    <p>
                      <strong>Measurement:</strong> Walk-shed calculated from actual reconstructed path speeds (avg 3.5 ft/s), not crow-flies radius.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wayfinding Tab */}
      {activeTab === 'wayfinding' && (
        <div className="space-y-6">
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
              Dead-end and Backtrack Index
            </h3>
            <p className="text-sm mb-6" style={{ color: 'var(--sca-muted)' }}>
              Score neighborhoods by fraction of paths that reverse, loop, or terminate without reaching a destination amenity. Rank wayfinding debt.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--sca-border)' }}>
                    <th className="text-left py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Zone</th>
                    <th className="text-left py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Floor</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Backtracks</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Dead Ends</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Total Paths</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Score</th>
                    <th className="text-left py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Remediation</th>
                  </tr>
                </thead>
                <tbody>
                  {wayfindingScores
                    .sort((a, b) => a.score - b.score)
                    .map((zone, idx) => {
                      const getScoreColor = (score: number) => {
                        if (score >= 80) return '#0A9B9B';
                        if (score >= 60) return '#8a7355';
                        return '#c33';
                      };

                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--sca-border)' }}>
                          <td className="py-3 px-4 font-medium" style={{ color: 'var(--sca-ink)' }}>{zone.zone}</td>
                          <td className="py-3 px-4" style={{ color: 'var(--sca-muted)' }}>{zone.floor}</td>
                          <td className="py-3 px-4 text-right" style={{ color: 'var(--sca-ink)' }}>{zone.backtracks}</td>
                          <td className="py-3 px-4 text-right" style={{ color: 'var(--sca-ink)' }}>{zone.deadEnds}</td>
                          <td className="py-3 px-4 text-right" style={{ color: 'var(--sca-muted)' }}>{zone.totalPaths}</td>
                          <td className="py-3 px-4 text-right">
                            <span className="font-bold text-lg" style={{ color: getScoreColor(zone.score) }}>
                              {zone.score}
                            </span>
                          </td>
                          <td className="py-3 px-4" style={{ color: 'var(--sca-ink)' }}>
                            <div style={{ maxWidth: 280 }}>{zone.remediation}</div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="mt-6 rounded-lg p-4" style={{ backgroundColor: 'var(--sca-hover)' }}>
              <div className="text-sm font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                Scoring Methodology
              </div>
              <div className="text-xs space-y-1" style={{ color: 'var(--sca-muted)' }}>
                <p>
                  <strong>Score:</strong> 100 minus (backtracks + dead ends) as percentage of total paths. Score 80+ is good wayfinding, 60-79 needs attention, below 60 requires intervention.
                </p>
                <p>
                  <strong>Backtrack:</strong> Path reverses direction more than 10 feet after apparent disorientation.
                </p>
                <p>
                  <strong>Dead end:</strong> Path terminates without reaching meeting room, desk, amenity, or exit.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lease Overlay Tab */}
      {activeTab === 'lease' && (
        <div className="space-y-6">
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
              Lease Stack and Cross-Demise Leakage
            </h3>
            <p className="text-sm mb-6" style={{ color: 'var(--sca-muted)' }}>
              Filter paths by floor stack or lease demise. Show cross-demise leakage (paths cutting through another tenant zone). Landlord-ops framing.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Navigation size={18} style={{ color: 'var(--sca-teal)' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>Total Leakage Events</span>
                </div>
                <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>94</div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>cross-demise paths today</div>
              </div>

              <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={18} style={{ color: '#8a7355' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>Avg Duration</span>
                </div>
                <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>28 sec</div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>per cross-tenant path</div>
              </div>

              <div className="rounded-lg border p-4" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp size={18} style={{ color: '#8a7355' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--sca-ink)' }}>Hotspot</span>
                </div>
                <div className="text-xl font-bold" style={{ color: '#8a7355' }}>Tenant B → Floor 1</div>
                <div className="text-xs" style={{ color: '#8a7355' }}>58 crossings to shared amenity</div>
              </div>
            </div>

            <div className="rounded-lg border p-6" style={{ backgroundColor: 'white', borderColor: 'var(--sca-border)' }}>
              <div className="text-sm font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Cross-Demise Leakage Detail</div>
              <div className="space-y-3">
                {leaseLeakage.map((leak, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
                    <div className="flex items-center gap-3 flex-1">
                      <span className="font-medium" style={{ color: 'var(--sca-ink)', minWidth: 200 }}>{leak.fromTenant}</span>
                      <ArrowRight size={16} style={{ color: 'var(--sca-muted)' }} />
                      <span className="font-medium" style={{ color: 'var(--sca-ink)', minWidth: 200 }}>{leak.toTenant}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="font-bold" style={{ color: 'var(--sca-teal)' }}>{leak.crossings}</div>
                        <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>crossings</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium" style={{ color: 'var(--sca-ink)' }}>{leak.avgDuration}</div>
                        <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>avg duration</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg p-4" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0', border: '1px solid' }}>
              <div className="text-sm font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                Landlord-Ops Considerations
              </div>
              <div className="text-xs space-y-2" style={{ color: '#8a7355' }}>
                <p>
                  <strong>Shared amenity impact:</strong> 58 crossings from Tenant B to shared Floor 1 amenities (cafe, restrooms) suggest high utilization. Consider lease language around shared-space allocation or pro-rata cost recovery.
                </p>
                <p>
                  <strong>Security and access:</strong> Cross-demise paths validate badge access patterns. 24 crossings from Tenant A to Tenant B may require review of access control policy if demise strictly prohibits inter-tenant circulation.
                </p>
                <p>
                  <strong>Space efficiency:</strong> Persistent cross-tenant paths reveal inefficient adjacency or missing amenities in tenant-exclusive zones. Path data informs future stack planning.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PathAnalysisStudio;
